import moment, { ISO_8601 } from "moment";
import "moment/locale/vi";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import TIcon from "../../components/ui/TIcon";
import * as SBService from "../../helpers/sportbookService";
import { getAssetFullUrl, toCurrency } from "../../helpers/utils";
import logoIcon from "statics/images/biadi-logo-icon.png";
import toast from "react-hot-toast";
import { addToCalendar } from "../../helpers/calendar";
import {
  NEWS_TYPE_EVENT,
  NEWS_TYPE_NEWS,
  NewsArticleInterface,
} from "../../interfaces/news";
import {
  bottomBarState,
  loaderState,
  showBottomBarState,
  userState,
} from "../../states/common";
import { LeagueFilterStatusEnum } from "../../shared/enums/LeagueFilterStatusEnum";
import ViecCanLam from "./ViecCanLam";
import { useFeaturedVenues } from "../../hooks/venue";

/**
 * Nuốt lỗi của một khối tin nhưng ghi lại vào console.
 *
 * Không có dòng log này thì API 404, thiếu cột trong DB và "chưa ghim bài nào"
 * đều cho ra một kết quả y hệt - khối rỗng, không thông báo, không dấu vết.
 */
const logAndFail = (label: string) => (error: any) => {
  // eslint-disable-next-line no-console
  console.error(`Không tải được ${label} cho trang chủ:`, error?.response?.status ?? "", error);

  return null;
};

/**
 * Dòng phụ dưới tiêu đề bài: chuyên mục và ngày đăng, bỏ phần nào trống.
 *
 * Cả hai đều không bắt buộc ở backoffice, nên ghép cứng "x · y" là ra những dòng
 * cụt đầu hoặc cụt đuôi kiểu " · 05/09/2026".
 */
const articleMeta = (article: NewsArticleInterface) =>
  [article.category, article.publishedAt ? moment(article.publishedAt).format("DD/MM/YYYY") : null]
    .filter(Boolean)
    .join(" · ");

/** Màn 01 — Trang chủ. Giải đấu, BXH, tin tức và sự kiện đều lấy từ API thật;
 *  tin tức/sự kiện là các bài được ghim ở backoffice. */
const HomePage = () => {
  const navigate = useNavigate();
  const [, setLoader] = useRecoilState(loaderState);
  const [, setActive] = useRecoilState(bottomBarState);
  const [, setBottomBar] = useRecoilState(showBottomBarState);
  const [user] = useRecoilState(userState);

  /* Hai khối giải lấy THẲNG từ server theo đúng bộ lọc của nó, không lấy chung
     một danh sách rồi tự chia theo ngày đăng ký ở trình duyệt. Server biết hai
     chuyện mà trình duyệt không biết: giải đã qua ngày kết thúc, và giải đã tìm
     ra nhà vô địch, đều không còn là giải "đang mở đăng ký" dù cột hạn đăng ký
     còn hiệu lực. Tự chia ở đây chính là lỗi đã gặp trên production: một giải đã
     kết thúc nằm cùng lúc ở "đang mở đăng ký" và ở "đã kết thúc". */
  const [openLeagues, setOpenLeagues] = useState<any[]>([]);
  const [upcomingLeagues, setUpcomingLeagues] = useState<any[]>([]);
  const [myLeagues, setMyLeagues] = useState<any[]>([]);
  const [topPlayers, setTopPlayers] = useState<any[]>([]);
  const [news, setNews] = useState<NewsArticleInterface[]>([]);
  const [events, setEvents] = useState<NewsArticleInterface[]>([]);
  // Tách "tải hỏng" khỏi "chưa ghim bài nào": hai chuyện đó dẫn tới hai cách xử
  // lý khác hẳn nhau, mà nếu cùng cho ra khối rỗng thì không ai phân biệt được.
  const [newsFailed, setNewsFailed] = useState(false);
  const [eventsFailed, setEventsFailed] = useState(false);

  /* Quán nổi bật — admin gắn cờ và sắp thứ tự ở backoffice.
     Tải riêng chứ không gộp vào load() bên dưới: khối này không có gì phụ thuộc
     bốn lời gọi kia, và hỏng thì chỉ mất khối của nó. */
  const featuredVenues = useFeaturedVenues(6);

  useEffect(() => {
    document.title = "Biadi — Trang chủ";
    setActive("home");
    setBottomBar(true);

    const load = async () => {
      setLoader(true);
      // Bốn lời gọi độc lập nhau; bắt lỗi từng cái để một khối hỏng không làm
      // trắng cả trang chủ.
      const [openRsp, upcomingRsp, playerRsp, newsRsp, eventRsp] = await Promise.all([
        SBService.getLeagues({
          page: 1,
          limit: 20,
          filter: LeagueFilterStatusEnum.REGISTRATION_OPEN,
        }).catch(logAndFail("giải đang mở đăng ký")),
        SBService.getLeagues({
          page: 1,
          limit: 20,
          filter: LeagueFilterStatusEnum.REGISTRATION_UPCOMING,
        }).catch(logAndFail("giải sắp mở đăng ký")),
        SBService.getTopPlayers(3).catch(() => null),
        SBService.getPinnedNews(NEWS_TYPE_NEWS).catch(logAndFail("tin tức")),
        SBService.getPinnedNews(NEWS_TYPE_EVENT).catch(logAndFail("sự kiện")),
      ]);
      setOpenLeagues(Array.isArray(openRsp?.data) ? openRsp.data : []);
      setUpcomingLeagues(Array.isArray(upcomingRsp?.data) ? upcomingRsp.data : []);
      setTopPlayers(playerRsp?.data ?? []);
      // callApi trả false khi chưa đăng nhập mà endpoint đòi token, và null khi
      // body rỗng - cả hai đều không phải mảng, tính là hỏng chứ không phải rỗng.
      setNews(newsRsp?.data ?? []);
      setEvents(eventRsp?.data ?? []);
      setNewsFailed(!Array.isArray(newsRsp?.data));
      setEventsFailed(!Array.isArray(eventRsp?.data));
      setLoader(false);
    };
    load();
  }, []);

  /* Giải đã đăng ký tải riêng, phụ thuộc hồ sơ.
     Layout nạp /players/me sau khi trang đã mount, nên phải chờ có user.id mới
     gọi được; để chung với khối trên thì cả trang chủ tải lại lần hai khi hồ sơ
     về. Lấy thẳng từ onlyMyLeagues chứ không lọc trong 20 giải đầu: giải mình
     tham gia có thể không nằm trong trang đó. */
  useEffect(() => {
    if (!user.id) {
      setMyLeagues([]);
      return;
    }
    let cancelled = false;
    const loadMine = async () => {
      /* Hỏi thẳng server "giải của tôi CHƯA kết thúc" thay vì lấy hết rồi tự lọc
         theo ngày ở đây. Server biết một chuyện mà trình duyệt không biết: giải đã
         tìm ra nhà vô địch là giải đã xong, kể cả khi chưa tới ngày kết thúc do ban
         tổ chức cấu hình. Tự lọc theo ngày ở đây là khối này nói "đang tham gia"
         trong khi tab Giải đấu đã xếp nó vào "đã kết thúc" - cùng một giải, hai câu
         trả lời khác nhau, đúng kiểu lỗi mà không ai thấy mâu thuẫn trong code. */
      const rsp = await SBService.getLeagues({
        page: 1,
        limit: 20,
        onlyMyLeagues: true,
        filter: LeagueFilterStatusEnum.NOT_ENDED,
      }).catch(logAndFail("giải đã đăng ký"));
      if (!cancelled) {
        setMyLeagues(Array.isArray(rsp?.data) ? rsp.data : []);
      }
    };
    loadMine();
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  /**
   * Ba khối chia theo VIỆC NGƯỜI DÙNG LÀM ĐƯỢC NGAY, không theo lịch thi đấu:
   * đang tham gia → đăng ký được → chưa mở đăng ký. Vì vậy hai khối sau bám mốc
   * ngày đăng ký chứ không phải ngày khai mạc — và chính server chia, bằng
   * REGISTRATION_OPEN / REGISTRATION_UPCOMING.
   */
  const { upcoming, openForReg, joined } = useMemo(() => {
    const withDates = (l: any) => ({
      ...l,
      start: moment(l.startDate, ISO_8601),
      end: moment(l.endDate, ISO_8601).endOf("day"),
      startReg: l.startRegistrationDate ? moment(l.startRegistrationDate, ISO_8601) : null,
      endReg: l.endRegistrationDate
        ? moment(l.endRegistrationDate, ISO_8601).endOf("day")
        : null,
    });

    /* Giải đang tham gia = đã đăng ký và CHƯA kết thúc. Không lọc lại theo ngày ở
       đây: danh sách này đã được server lọc bằng NOT_ENDED, vốn xét cả ngày kết
       thúc lẫn việc giải đã có nhà vô địch hay chưa. Lọc thêm một lớp theo ngày là
       dựng lại một nửa luật của server, rồi lệch khi nửa kia đổi. */
    const mine = myLeagues.map(withDates);
    const mineIds = new Set(mine.map((l) => l.id));

    // Giải đã đăng ký chỉ xuất hiện ở khối "đang tham gia" — để nguyên ở hai
    // khối kia là cùng một giải hiện hai lần với hai lời kêu gọi khác nhau.
    const chuaDangKy = (list: any[]) => list.map(withDates).filter((l) => !mineIds.has(l.id));

    return {
      upcoming: chuaDangKy(upcomingLeagues)
        .sort((a, b) => (a.startReg?.valueOf() ?? 0) - (b.startReg?.valueOf() ?? 0))
        .slice(0, 3),
      openForReg: chuaDangKy(openLeagues),
      joined: mine.sort((a, b) => a.end.valueOf() - b.end.valueOf()),
    };
  }, [openLeagues, upcomingLeagues, myLeagues]);

  /** Nhắc ngày mở đăng ký, không phải ngày thi đấu — đó mới là lúc cần hành động. */
  const remindMe = (league: any) => {
    if (!league.startReg) {
      toast.error("Giải chưa công bố ngày mở đăng ký");
      return;
    }
    addToCalendar({
      id: league.id,
      title: `Mở đăng ký: ${league.name}`,
      start: league.startReg.toDate(),
      end: league.startReg.clone().add(1, "hour").toDate(),
      location: league.venues?.[0]?.name ?? null,
      description: `Giải ${league.name} mở đăng ký. Thi đấu ${league.start.format(
        "DD/MM/YYYY"
      )} – ${league.end.format("DD/MM/YYYY")}.`,
      url: `${window.location.origin}/league/${league.id}`,
    });
    toast.success("Đã tạo lời nhắc, mở file để thêm vào lịch");
  };

  return (
    <>
      {/* ── Thanh đầu trang ── */}
      <header className="topbar bleed">
        <button
          type="button"
          className="brand"
          onClick={() => navigate("/")}
          aria-label="Biadi"
        >
          <img src={logoIcon} alt="" />
          <span>BIADI</span>
        </button>
        <div className="actions">
          <button
            type="button"
            className="icon-btn"
            aria-label="Tìm kiếm"
            onClick={() => navigate("/league")}
          >
            <TIcon name="search" />
          </button>
          {user.id ? (
            <button
              type="button"
              className="avatar"
              style={{ width: 36, height: 36 }}
              onClick={() => navigate("/profile")}
              aria-label="Tài khoản"
            >
              {user.profilePicture ? (
                <img src={user.profilePicture} alt="" />
              ) : (
                <TIcon name="user" />
              )}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => navigate("/auth/login")}
            >
              ĐĂNG NHẬP
            </button>
          )}
        </div>
      </header>

      {/* ── Cần bạn thực hiện ──
           Trên cùng vì đây là thứ DUY NHẤT trên trang chủ cần người dùng làm gì đó
           ngay; mấy khối dưới đều là thông tin để xem. Tự ẩn khi không còn việc. */}
      <ViecCanLam />

      {/* ── Giải đang tham gia ──
           Đặt trên cùng vì đây là việc của riêng người dùng: giải họ đã bỏ tiền
           đăng ký và cần biết đang tới bước nào. */}
      {joined.length > 0 ? (
        <section className="section">
          <div className="section-head">
            <h2>GIẢI ĐANG THAM GIA</h2>
            {/* Chỉ có nút khi thật sự còn giải chưa hiện ở đây — quá 3 giải mới
                cắt bớt, dưới đó thì "Tất cả" chẳng dẫn tới gì mới. */}
            {joined.length > 3 ? (
              <button type="button" onClick={() => navigate("/league?tab=joined")}>
                Tất cả <TIcon name="chevron-right" />
              </button>
            ) : null}
          </div>

          {joined.slice(0, 3).map((l) => (
            <div
              key={l.id}
              className="ui-card event-card"
              onClick={() => navigate(`/league/${l.id}`)}
            >
              <img
                className="date-box"
                alt=""
                src={l.logo ? getAssetFullUrl(l.logo) : logoIcon}
                style={{ objectFit: "cover" }}
              />
              <div className="info">
                <span className="name">{l.name}</span>
                <span className="meta">
                  {l.start.format("DD/MM")}–{l.end.format("DD/MM/YY")}
                  {l.venues?.[0]?.name ? ` · ${l.venues[0].name}` : ""}
                </span>
              </div>
              <span style={{ color: "var(--biadi-steel)", fontSize: 18 }}>
                <TIcon name="chevron-right" />
              </span>
            </div>
          ))}
        </section>
      ) : null}

      {/* ── Đang mở đăng ký ──
           Carousel: nhiều giải có thể mở đăng ký cùng lúc, chọn sẵn một giải
           làm "nổi bật" thì các giải còn lại không có đường nào vào từ đây. */}
      {openForReg.length > 0 ? (
        <section className="section">
          <div className="section-head">
            <h2>ĐANG MỞ ĐĂNG KÝ</h2>
            <button type="button" onClick={() => navigate("/league?tab=open")}>
              Tất cả <TIcon name="chevron-right" />
            </button>
          </div>

          <div className="hero-carousel">
            {openForReg.map((l) => (
              <button
                key={l.id}
                type="button"
                className="hero"
                onClick={() => navigate(`/league/${l.id}`)}
              >
                {l.banner ? (
                  <img src={getAssetFullUrl(l.banner)} alt={l.name} />
                ) : null}
                <div className="overlay" />
                <div className="content">
                  <span className="ui-badge">ĐANG MỞ ĐĂNG KÝ</span>
                  <span className="title">{l.name}</span>
                  <span className="meta">
                    {l.venues?.[0]?.name ?? "Đang cập nhật địa điểm"} ·{" "}
                    {l.endReg ? `Hạn đăng ký ${l.endReg.format("DD/MM")}` : ""}
                    {l.fee ? ` · Lệ phí ${toCurrency(l.fee)}đ` : ""}
                  </span>
                  <span className="hero-cta">ĐĂNG KÝ</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Giải đấu sắp tới ──
           Chưa tới ngày mở đăng ký, nên chưa làm gì được ngoài việc nhớ ngày:
           CTA là đặt lịch chứ không phải đăng ký. Khối rỗng thì ẩn hẳn. */}
      {upcoming.length > 0 ? (
        <section className="section">
          <div className="section-head">
            <h2>GIẢI ĐẤU SẮP TỚI</h2>
            <button type="button" onClick={() => navigate("/league?tab=upcoming")}>
              Tất cả <TIcon name="chevron-right" />
            </button>
          </div>

          {upcoming.map((l) => (
            <div key={l.id} className="ui-card event-card">
              <div className="date-box" onClick={() => navigate(`/league/${l.id}`)}>
                <span className="d">{l.startReg.format("DD")}</span>
                <span className="m">TH{l.startReg.format("M")}</span>
              </div>
              <div className="info" onClick={() => navigate(`/league/${l.id}`)}>
                <span className="name">{l.name}</span>
                <span className="meta">
                  Mở đăng ký {l.startReg.format("DD/MM")}
                  {l.fee ? ` · Lệ phí ${toCurrency(l.fee)}đ` : ""}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => remindMe(l)}
              >
                ĐẶT LỊCH
              </button>
            </div>
          ))}
        </section>
      ) : null}

      {/* ── Tin tức ──
           Bài lên đây là bài được ghim ở backoffice (Nội dung → Tin tức), tối đa
           ba bài. Không ghim bài nào thì khối ẩn hẳn chứ không rơi về "bài mới
           nhất": làm vậy thì công tắc ghim chẳng còn nghĩa gì. */}
      {newsFailed ? (
        <section className="section">
          <div className="section-head">
            <h2>TIN TỨC</h2>
          </div>
          <div className="ui-card empty-state">
            <TIcon name="alert-triangle" />
            <div className="t">Không tải được tin tức</div>
            <div className="h">Thử lại sau ít phút.</div>
          </div>
        </section>
      ) : null}

      {news.length ? (
        <section className="section">
          <div className="section-head">
            <h2>TIN TỨC</h2>
            <button type="button" onClick={() => navigate("/news")}>
              Tất cả <TIcon name="chevron-right" />
            </button>
          </div>
          {news.map((n) => (
            <button
              key={n.id}
              type="button"
              className="news-row"
              onClick={() => navigate(`/news/${n.slug}`)}
            >
              <div className="thumb">
                {n.image ? (
                  <img src={getAssetFullUrl(n.image)} alt="" />
                ) : (
                  <span className="thumb-fallback">
                    <img src={logoIcon} alt="" />
                  </span>
                )}
              </div>
              <div className="info">
                <span className="title">{n.title}</span>
                <span className="meta">{articleMeta(n)}</span>
              </div>
            </button>
          ))}
        </section>
      ) : null}

      {/* ── Sự kiện ── (cùng luật ghim với khối Tin tức ở trên) */}
      {eventsFailed ? (
        <section className="section">
          <div className="section-head">
            <h2>SỰ KIỆN</h2>
          </div>
          <div className="ui-card empty-state">
            <TIcon name="alert-triangle" />
            <div className="t">Không tải được sự kiện</div>
            <div className="h">Thử lại sau ít phút.</div>
          </div>
        </section>
      ) : null}

      {events.length ? (
        <section className="section">
          <div className="section-head">
            <h2>SỰ KIỆN</h2>
            <button
              type="button"
              onClick={() => navigate(`/news?type=${NEWS_TYPE_EVENT}`)}
            >
              Tất cả <TIcon name="chevron-right" />
            </button>
          </div>
          <div className="grid-2">
            {events.map((ev) => (
              <button
                key={ev.id}
                type="button"
                className="ui-card mini-card"
                onClick={() => navigate(`/news/${ev.slug}`)}
              >
                <div className="thumb">
                  {ev.image ? (
                    <img src={getAssetFullUrl(ev.image)} alt="" />
                  ) : (
                    <span className="thumb-fallback">
                      <img src={logoIcon} alt="" />
                    </span>
                  )}
                </div>
                <div className="info">
                  <span className="title">{ev.title}</span>
                  <span className="meta">{articleMeta(ev)}</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Bảng xếp hạng rút gọn ── */}
      <section className="section">
        <div className="section-head">
          <h2>BẢNG XẾP HẠNG BIADI</h2>
          <button type="button" onClick={() => navigate("/bxh")}>
            Xem BXH <TIcon name="chevron-right" />
          </button>
        </div>
        <div className="ui-card list">
          {topPlayers.length ? (
            topPlayers.map((p, i) => (
              /* Không có nguồn cho mức tăng/giảm hạng của người khác
                 (/players/me/rank-history chỉ có của chính mình), nên bỏ hẳn
                 cột mũi tên thay vì vẽ một chiều mũi tên bịa theo thứ tự. */
              <div key={p.id ?? i} className="list-row">
                <span className="rank-pos">{i + 1}</span>
                <span className="rank-name">{p.name}</span>
                <span className="rank-pts">{toCurrency(p.score ?? 0)} đ</span>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <TIcon name="list-numbers" />
              <div className="t">Chưa có dữ liệu xếp hạng</div>
            </div>
          )}
        </div>
      </section>

      {/* ── Quán nổi bật ──
           Cửa vào trang giới thiệu quán (/venue/:id). Nằm cuối cùng vì đây là
           khối để xem thêm sau khi đã lướt hết giải đấu, tin tức và BXH, không
           phải việc người dùng mở app để làm.

           Danh sách do backoffice chọn, không phải "quán nào nhiều cơ sở nhất"
           — front office giữ nguyên thứ tự nhận được. */}
      {featuredVenues.length > 0 ? (
        <section className="section">
          <div className="section-head">
            <h2>QUÁN NỔI BẬT</h2>
            {/* Khối này chỉ hiện những quán admin chọn; /venue mới là toàn bộ. */}
            <button type="button" onClick={() => navigate("/venue")}>
              Tất cả <TIcon name="chevron-right" />
            </button>
          </div>

          <div className="hero-carousel">
            {featuredVenues.map((v) => (
              <button
                key={v.id}
                type="button"
                className="hero venue-hero"
                onClick={() => navigate(`/venue/${v.id}`)}
              >
                {v.coverImage ? <img src={v.coverImage} alt="" /> : null}
                <div className="overlay" />
                {v.logo ? (
                  <img className="venue-mark" src={v.logo} alt="" aria-hidden />
                ) : null}
                <div className="content">
                  {v.area ? <span className="ui-badge">{v.area}</span> : null}
                  <span className="title">{v.name}</span>
                  <span className="meta">
                    {v.tableCount} bàn
                    {v.headline ? ` · ${v.headline}` : ""}
                  </span>
                  <span className="hero-cta">XEM QUÁN</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Kêu gọi đăng ký ── */}
      {!user.id ? (
        <section className="cta-card">
          <h2>TRỞ THÀNH THÀNH VIÊN BIADI</h2>
          <p>
            Lưu hạng cơ, theo dõi lịch sử thi đấu và đăng ký giải chỉ với một
            chạm.
          </p>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={{ letterSpacing: "2px", padding: "11px 20px" }}
            onClick={() => navigate("/auth/register")}
          >
            ĐĂNG KÝ MIỄN PHÍ
          </button>
        </section>
      ) : null}

      <footer className="app-footer">
        <span className="copy">
          © {moment().year()} Biadi · Nền tảng Billiards số
        </span>
        <span className="socials">
          <TIcon name="brand-facebook" />
          <TIcon name="brand-youtube" />
          <TIcon name="brand-tiktok" />
        </span>
      </footer>

    </>
  );
};

export default HomePage;
